import classNames from 'classnames'
import React, { useState } from 'react'

import { Conversation } from './types'

interface Props {
  conversations: Conversation[]
  currentConversationId: string | null
  onSelectConversation: (conversationId: string | null) => Promise<void>
  onRenameConversation: (
    conversationId: string,
    newTitle: string
  ) => Promise<void>
  onDeleteConversation: (conversationId: string) => Promise<void>
  onDeleteAllConversations: () => Promise<void>
  onCreateNewConversation: () => void
  className?: string
}

const ConversationManager: React.FC<Props> = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onRenameConversation,
  onDeleteConversation,
  onDeleteAllConversations,
  onCreateNewConversation,
  className,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const currentConversation = conversations.find(
    (conv) => conv.id === currentConversationId
  )

  const handleRenameStart = (conversation: Conversation) => {
    setRenamingId(conversation.id)
    setRenameValue(conversation.title)
  }

  const handleRenameSubmit = async () => {
    if (renamingId && renameValue.trim()) {
      try {
        await onRenameConversation(renamingId, renameValue.trim())
      } catch (error) {
        console.error('Error renaming conversation:', error)
      }
    }
    setRenamingId(null)
    setRenameValue('')
  }

  const handleRenameCancel = () => {
    setRenamingId(null)
    setRenameValue('')
  }

  const formatDate = (date: Date) => {
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = diffInMs / (1000 * 60)
    const diffInHours = diffInMinutes / 60
    const diffInDays = diffInHours / 24

    if (diffInMinutes < 1) {
      return 'agora'
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m atrás`
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h atrás`
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d atrás`
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }
  }

  return (
    <div className={classNames('relative', className)}>
      <div className="flex items-center space-x-2">
        {/* Conversation Selector */}
        <div className="relative flex-1">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50 focus:border-info-dark focus:outline-none focus:ring-1 focus:ring-info-dark"
          >
            <span className="truncate">
              {currentConversation?.title || 'Nova Conversa'}
            </span>
            <svg
              className={classNames(
                'h-4 w-4 transition-transform',
                isDropdownOpen ? 'rotate-180' : ''
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {/* New Conversation Option */}
              <button
                onClick={() => {
                  onCreateNewConversation()
                  setIsDropdownOpen(false)
                }}
                className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <svg
                  className="mr-2 h-4 w-4 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span className="font-medium text-green-600">
                  Nova Conversa
                </span>
              </button>

              {conversations.length > 0 && <hr className="border-gray-200" />}

              {/* Existing Conversations */}
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={classNames(
                    'group flex items-center justify-between px-3 py-2 hover:bg-gray-50',
                    currentConversationId === conversation.id
                      ? 'bg-blue-50'
                      : ''
                  )}
                >
                  {renamingId === conversation.id ? (
                    <div className="flex flex-1 flex-col">
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleRenameSubmit()
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault()
                            handleRenameCancel()
                          }
                        }}
                        onBlur={handleRenameSubmit}
                        className="w-full rounded border border-gray-300 px-1 py-0.5 text-sm"
                        autoFocus
                      />
                      <div className="mt-1 flex items-center space-x-1 text-xs text-gray-500">
                        <span className="max-w-[200px] truncate">
                          {conversation.lastMessage}
                        </span>
                        <span>•</span>
                        <span>{formatDate(conversation.updatedAt)}</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          await onSelectConversation(conversation.id)
                          setIsDropdownOpen(false)
                        } catch (error) {
                          console.error('Error loading conversation:', error)
                        }
                      }}
                      className="flex-1 text-left"
                    >
                      <div className="flex flex-col">
                        <span className="truncate text-sm font-medium">
                          {conversation.title}
                        </span>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <span className="max-w-[200px] truncate">
                            {conversation.lastMessage}
                          </span>
                          <span>•</span>
                          <span>{formatDate(conversation.updatedAt)}</span>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRenameStart(conversation)
                      }}
                      className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                      title="Renomear"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (
                          window.confirm(
                            'Tem certeza que deseja excluir esta conversa?'
                          )
                        ) {
                          try {
                            await onDeleteConversation(conversation.id)
                          } catch (error) {
                            console.error('Error deleting conversation:', error)
                          }
                        }
                      }}
                      className="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600"
                      title="Excluir"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {conversations.length > 0 && (
                <>
                  <hr className="border-gray-200" />
                  <button
                    onClick={async () => {
                      if (
                        window.confirm(
                          'Tem certeza que deseja excluir todas as conversas? Esta ação não pode ser desfeita.'
                        )
                      ) {
                        try {
                          await onDeleteAllConversations()
                          setIsDropdownOpen(false)
                        } catch (error) {
                          console.error(
                            'Error deleting all conversations:',
                            error
                          )
                        }
                      }
                    }}
                    className="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Excluir Todas
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <button
          onClick={onCreateNewConversation}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
          title="Nova Conversa"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>
      </div>

      {/* Click outside to close dropdown */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  )
}

export default ConversationManager
